import { useCallback, useEffect, useRef, useState } from 'react';
import { storage } from '../../utils/platform';
import { useCurrentRestaurant } from '../contexts/CurrentRestaurantContext';
import { API_BASE } from '../../config/api';

/**
 * useFeedbacks — feedbacks privés interceptés par le pare-feu Revyo.
 *
 * L'app affichait jusqu'ici le seul COMPTEUR de feedbacks (KPIGrid) sans aucun
 * moyen de lire ce que les clients avaient écrit : il fallait ouvrir le web.
 * C'est pourtant le cœur de la promesse produit — voir ce qui aurait fini en
 * avis public négatif.
 *
 * Endpoints :
 *   GET /api/dashboard/feedbacks            → { feedbacks, count_total, count_mois }
 *   PUT /api/dashboard/feedback/:id/treat   → marque un feedback comme traité
 *
 * Refetch automatique au changement de restaurant, comme les autres hooks.
 */
export function useFeedbacks() {
  const { currentRestaurantId } = useCurrentRestaurant();
  const [feedbacks, setFeedbacks] = useState([]);
  const [countTotal, setCountTotal] = useState(0);
  const [countMois, setCountMois] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cancelRef = useRef(false);

  const getHeaders = useCallback(async () => {
    const token = await storage.get('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      // Multi-resto : sans cet en-tête, le backend retomberait sur le premier
      // restaurant du compte au lieu de celui affiché à l'écran.
      ...(currentRestaurantId ? { 'X-Restaurant-Id': currentRestaurantId } : {}),
    };
  }, [currentRestaurantId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_BASE}/api/dashboard/feedbacks`, { headers });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.error || `HTTP ${res.status}`);
      if (cancelRef.current) return;
      setFeedbacks(Array.isArray(payload?.feedbacks) ? payload.feedbacks : []);
      setCountTotal(payload?.count_total ?? 0);
      setCountMois(payload?.count_mois ?? 0);
    } catch (e) {
      if (!cancelRef.current) setError(e.message || 'Chargement impossible');
    } finally {
      if (!cancelRef.current) setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    cancelRef.current = false;
    load();
    return () => { cancelRef.current = true; };
  }, [load]);

  /**
   * Marque un feedback traité. Mise à jour optimiste : le geste doit répondre
   * instantanément au doigt. En cas d'échec, on restaure l'état précédent
   * plutôt que de laisser une coche mensongère.
   */
  const markTreated = useCallback(async (id) => {
    const before = feedbacks;
    setFeedbacks((list) => list.map(f => (f.id === id ? { ...f, traite: true } : f)));
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_BASE}/api/dashboard/feedback/${id}/treat`, {
        method: 'PUT', headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return true;
    } catch (e) {
      console.warn('[useFeedbacks] markTreated échoué:', e.message);
      setFeedbacks(before);
      return false;
    }
  }, [feedbacks, getHeaders]);

  return {
    feedbacks, countTotal, countMois,
    loading, error,
    refresh: load, markTreated,
  };
}

export default useFeedbacks;
